package songwritersnotebook.app.screens

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Article
import androidx.compose.material.icons.filled.*
import songwritersnotebook.app.utils.NetworkUtils
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.viewmodels.HomeViewModel
import songwritersnotebook.app.viewmodels.HomeUiState
import songwritersnotebook.app.viewmodels.SongToTextState
import org.jetbrains.compose.resources.painterResource
import songwritersnotebookapp.composeapp.generated.resources.Res
import songwritersnotebookapp.composeapp.generated.resources.ic_logo_fg
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import songwritersnotebook.app.utils.PlatformUtils
import songwritersnotebook.app.utils.rememberFileSaverLauncher
import songwritersnotebook.app.components.ShareSongDialog
import songwritersnotebook.app.components.DeleteConfirmationDialog
import songwritersnotebook.app.components.LoadingOverlay
import songwritersnotebook.app.tutorial.TutorialManager
import songwritersnotebook.app.tutorial.TutorialStep
import songwritersnotebook.app.tutorial.tutorialTarget
import songwritersnotebook.app.viewmodels.SongTab

import androidx.compose.ui.window.Dialog
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily

import songwritersnotebook.app.platform.PlatformAdView
import songwritersnotebook.app.components.MobileAppCtaBanner

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HomeScreen(
    songs: List<Song>,
    isLoading: Boolean = false,
    error: String? = null,
    onEditSong: (Song) -> Unit = {},
    onStudioSong: (Song) -> Unit = {},
    onNavigateToSettings: () -> Unit = {},
    onNavigateToTutorial: () -> Unit = {},
    onDeleteSong: (String) -> Unit = {},
    onRefresh: () -> Unit = {},
    onExportText: (Song) -> Unit = {},
    onExportAudio: (Song) -> Unit = {},
    // Added for full feature set
    viewModel: HomeViewModel? = null,
    onSearchQueryChange: (String) -> Unit = {},
    searchQuery: String = "",
    subscriptionTier: songwritersnotebook.app.billing.SubscriptionTier = songwritersnotebook.app.billing.SubscriptionTier.FREE,
    onGoogleSignIn: () -> Unit = {},
    onNavigateToSubscription: () -> Unit = {}
) {
    var activeSlideMenuId by remember { mutableStateOf<String?>(null) }
    val platformUtils = remember { PlatformUtils() }
    val currentStep by TutorialManager.currentStep.collectAsState()
    val isWeb = remember { songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true) }
    val preferencesManager = remember { songwritersnotebook.app.utils.PreferencesManager() }
    var showDailyReminderPrompt by remember { mutableStateOf(false) }
    var showIosInstallDialog by remember { mutableStateOf(false) }
    var showMobileAppCta by remember { mutableStateOf(isWeb && !preferencesManager.hasDismissedMobileAppCTA && platformUtils.getWebBrowserOS() != null) }

    // UI States from ViewModel
    val uiState = viewModel?.uiState?.collectAsState()?.value ?: HomeUiState()
    val generatedText = viewModel?.generatedSongText?.collectAsState()?.value ?: ""
    val isExporting = uiState.isExporting
    val exportError = uiState.error

    val notificationPermissionLauncher = songwritersnotebook.app.utils.rememberNotificationPermissionLauncher(
        onGranted = { showDailyReminderPrompt = true },
        onDenied = { 
            // Even if denied, show the reminder prompt (it's for the app-level toggle)
            showDailyReminderPrompt = true 
        }
    )

    // First Launch Prompt Logic
    LaunchedEffect(uiState.currentUserId) {
        if (uiState.currentUserId != null && !preferencesManager.hasPromptedForNotifications && !isWeb) {
            notificationPermissionLauncher()
            preferencesManager.hasPromptedForNotifications = true
        }
    }
    
    // Tutorial Registration
    LaunchedEffect(Unit) {
        TutorialManager.registerAction(TutorialStep.HOME_SETTINGS_BUTTON) {
            onNavigateToSettings()
        }
        TutorialManager.registerEnterAction(TutorialStep.SETTINGS_GENERAL) {
            onNavigateToSettings()
        }
        // HOME_SONG_CARD just highlights details now. Next step handles tap.
        TutorialManager.registerAction(TutorialStep.HOME_SONG_CARD) { }
        
        TutorialManager.registerAction(TutorialStep.HOME_TAP_SONG) {
            val tutorialSongId = TutorialManager.TUTORIAL_SONG_ID
            // Use live flow to get current list instead of captured 'songs' parameter
            val currentSongs = viewModel?.filteredSongs?.value ?: emptyList()
            val tutorialSong = currentSongs.find { it.id == tutorialSongId }
            if (tutorialSong != null) {
                onEditSong(tutorialSong)
            } else {
                val first = currentSongs.firstOrNull()
                if (first != null) onEditSong(first)
            }
        }
        TutorialManager.registerAction(TutorialStep.HOME_SHARED_TAB) {
            TutorialManager.nextStep()
        }
    }

    LaunchedEffect(currentStep) {
        if (currentStep == TutorialStep.WELCOME) {
            viewModel?.onTabSelected(SongTab.MY_SONGS)
        }
    }
    
    // Audio Export Launcher
    val songForAudio = uiState.songForAudioExport
    val audioLauncher = rememberFileSaverLauncher(
        suggestedName = "${songForAudio?.title ?: "song"}.m4a",
        mimeType = "audio/mp4"
    ) { uri ->
        songForAudio?.let { song ->
            viewModel?.exportSong(song, uri)
        }
    }

    // Effect to trigger audio file picker
    LaunchedEffect(songForAudio) {
        if (songForAudio != null) {
            audioLauncher()
        }
    }

    Scaffold(
        topBar = { HomeTopBar() },
        bottomBar = {
            BottomAppBar(
                containerColor = MaterialTheme.colorScheme.surface,
                contentPadding = PaddingValues(0.dp),
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceAround,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Song List Button
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight()
                            .clickable { /* Already on this screen */ },
                        contentAlignment = Alignment.Center
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            Icon(
                                Icons.Default.Home,
                                contentDescription = "Song List",
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(30.dp)
                            )
                            Text(
                                "Song List",
                                fontSize = 14.sp,
                                color = MaterialTheme.colorScheme.primary,
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                    // Spacer to create the gap for the FAB
                    Spacer(modifier = Modifier.weight(0.3f))
                    // Settings Button
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight()
                            .clickable { 
                                if (currentStep == TutorialStep.HOME_SETTINGS_BUTTON) {
                                    TutorialManager.nextStep()
                                }
                                onNavigateToSettings() 
                            }
                            .tutorialTarget(TutorialStep.HOME_SETTINGS_BUTTON),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            Icon(
                                Icons.Default.Settings,
                                contentDescription = "Settings",
                                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.size(30.dp)
                            )
                            Text(
                                "Settings",
                                fontSize = 14.sp,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                }
            }
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = { 
                    if (currentStep == TutorialStep.HOME_CREATE_SONG) {
                        TutorialManager.nextStep()
                    }
                    viewModel?.createNewSong { newId ->
                        val dummy = Song(id = newId, title = "New Song")
                        onEditSong(dummy)
                    }
                },
                shape = CircleShape,
                containerColor = MaterialTheme.colorScheme.primary,
                contentColor = MaterialTheme.colorScheme.onPrimary,
                elevation = FloatingActionButtonDefaults.elevation(defaultElevation = 4.dp),
                modifier = Modifier
                    .size(84.dp)
                    .offset(y = 52.dp) // Push it down into the cutout
                    .tutorialTarget(TutorialStep.HOME_CREATE_SONG)
            ) {
                Icon(
                    imageVector = Icons.Default.Add,
                    contentDescription = "New Song",
                    modifier = Modifier.size(52.dp)
                )
            }
        },
        floatingActionButtonPosition = FabPosition.Center
    ) { paddingValues ->
        Box(modifier = Modifier.fillMaxSize()) {
            if (showIosInstallDialog) {
                AlertDialog(
                    onDismissRequest = { 
                        showIosInstallDialog = false
                        preferencesManager.hasDismissedMobileAppCTA = true
                        showMobileAppCta = false 
                    },
                    title = { Text("Install App") },
                    text = { Text("To install Songwriter's Notebook on your iPhone or iPad, tap the Share icon at the bottom of Safari and select 'Add to Home Screen'.") },
                    confirmButton = {
                        TextButton(onClick = { 
                            showIosInstallDialog = false
                            preferencesManager.hasDismissedMobileAppCTA = true
                            showMobileAppCta = false 
                        }) {
                            Text("Got it")
                        }
                    }
                )
            }
            Column(
                modifier = Modifier
                    .padding(paddingValues)
                    .padding(bottom = if (isWeb) 80.dp else 32.dp) // Extra padding for FAB/WebFooter
            ) {
                // Banner Ad for Free Users
                if (subscriptionTier == songwritersnotebook.app.billing.SubscriptionTier.FREE && !isWeb && uiState.currentUserId != null) {
                    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                        PlatformAdView()
                    }
                }
                
                if (isWeb) {
                    if (showMobileAppCta) {
                        MobileAppCtaBanner(
                            os = platformUtils.getWebBrowserOS() ?: "",
                            onDismiss = {
                                preferencesManager.hasDismissedMobileAppCTA = true
                                showMobileAppCta = false
                            },
                            onAction = {
                                if (platformUtils.getWebBrowserOS() == "Android") {
                                    platformUtils.openUrl("https://play.google.com/store/apps/details?id=songwritersnotebook.app")
                                    preferencesManager.hasDismissedMobileAppCTA = true
                                    showMobileAppCta = false
                                } else {
                                    showIosInstallDialog = true
                                }
                            }
                        )
                    }

                    if (uiState.currentUserId == null) {
                        Surface(
                            modifier = Modifier.fillMaxWidth().padding(16.dp),
                            color = MaterialTheme.colorScheme.errorContainer,
                            shape = RoundedCornerShape(8.dp),
                            tonalElevation = 2.dp
                        ) {
                            Row(
                                modifier = Modifier.padding(12.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(Icons.Default.Warning, contentDescription = null, tint = MaterialTheme.colorScheme.onErrorContainer)
                                Spacer(Modifier.width(12.dp))
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(
                                        "Guest Session",
                                        style = MaterialTheme.typography.labelLarge,
                                        color = MaterialTheme.colorScheme.onErrorContainer,
                                        fontWeight = FontWeight.Bold
                                    )
                                    Text(
                                        "Work started as a guest will be lost when you close this tab. Sign in to save and sync your songs.",
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onErrorContainer
                                    )
                                }
                                TextButton(onClick = onGoogleSignIn) {
                                    Text("Sign In")
                                }
                            }
                        }
                    } else if (subscriptionTier != songwritersnotebook.app.billing.SubscriptionTier.STUDIO) {
                        Surface(
                            modifier = Modifier.fillMaxWidth().padding(16.dp),
                            color = MaterialTheme.colorScheme.tertiaryContainer,
                            shape = RoundedCornerShape(8.dp),
                            tonalElevation = 2.dp
                        ) {
                            Row(
                                modifier = Modifier.padding(12.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(Icons.Default.Info, contentDescription = null, tint = MaterialTheme.colorScheme.onTertiaryContainer)
                                Spacer(Modifier.width(12.dp))
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(
                                        "Studio Subscription Required",
                                        style = MaterialTheme.typography.labelLarge,
                                        color = MaterialTheme.colorScheme.onTertiaryContainer,
                                        fontWeight = FontWeight.Bold
                                    )
                                    Text(
                                        "You are signed in, but saving and syncing on the web is exclusive to Studio subscribers. Upgrade to keep your changes!",
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onTertiaryContainer
                                    )
                                }
                                TextButton(onClick = onNavigateToSubscription) {
                                    Text("Upgrade")
                                }
                            }
                        }
                    }
                }

                // Tabs (Only if logged in)
                if (uiState.currentUserId != null) {
                    val invitations by viewModel?.invitations?.collectAsState() ?: mutableStateOf(emptyList())
                    
                    PrimaryTabRow(
                        selectedTabIndex = uiState.selectedTab.ordinal,
                        containerColor = MaterialTheme.colorScheme.surface,
                        contentColor = MaterialTheme.colorScheme.primary
                    ) {
                        Tab(
                            selected = uiState.selectedTab == SongTab.MY_SONGS,
                            onClick = { viewModel?.onTabSelected(SongTab.MY_SONGS) },
                            text = { Text("My Songs") }
                        )
                        Tab(
                            selected = uiState.selectedTab == SongTab.SHARED,
                            onClick = { viewModel?.onTabSelected(SongTab.SHARED) },
                            modifier = Modifier.tutorialTarget(TutorialStep.HOME_SHARED_TAB),
                            text = { 
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Text("Shared")
                                    if (invitations.isNotEmpty()) {
                                        Spacer(Modifier.width(6.dp))
                                        Badge { 
                                            Text(invitations.size.toString()) 
                                        }
                                    }
                                }
                            }
                        )
                    }
                }

                HomeSearchBar(
                    query = searchQuery,
                    onQueryChange = onSearchQueryChange,
                    modifier = Modifier.tutorialTarget(TutorialStep.HOME_SEARCH)
                )
                
                if (isLoading) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                } else if (songs.isEmpty() && viewModel?.invitations?.collectAsState()?.value.isNullOrEmpty()) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = if (uiState.selectedTab == SongTab.SHARED) "No shared songs." else "No songs yet. Tap + to create.",
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                } else {
                    LazyColumn(
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        // Invitations Section
                        if (uiState.selectedTab == SongTab.SHARED) {
                            item {
                                InvitationsSection(viewModel = viewModel, subscriptionTier = subscriptionTier)
                            }
                        }

                        items(songs.size, key = { songs[it].id }) { index ->
                            val song = songs[index]
                            val isTutorialSong = song.id == TutorialManager.TUTORIAL_SONG_ID
                            
                            SongListItem(
                                song = song,
                                onSongClick = { 
                                    if (currentStep == TutorialStep.HOME_TAP_SONG) {
                                        TutorialManager.nextStep()
                                    }
                                    onEditSong(song) 
                                },
                                onSongDeleteRequest = { onDeleteSong(song.id) },
                                onSongToTextRequest = { onExportText(song) },
                                onSongExportRequest = { onExportAudio(song) },
                                onSongShareRequest = { viewModel?.onShareRequest(song, subscriptionTier) },
                                isMenuExpanded = activeSlideMenuId == song.id,
                                onSlideMenuToggled = { activeSlideMenuId = if (activeSlideMenuId == song.id) null else song.id },
                                isTutorialSong = isTutorialSong,
                                currentUserId = uiState.currentUserId,
                                subscriptionTier = subscriptionTier
                            )
                        }
                    }
                }
            }

            // --- Dialogs ---

            // Text Export Dialog
            if (uiState.songForTextExport != null) {
                val song = uiState.songForTextExport
                val options = uiState.songToTextState
                val canExportTabs = true // Assume true or check subscription features if needed
                
                AlertDialog(
                    onDismissRequest = { viewModel?.onSongToTextDismiss() },
                    title = { Text("Export '${song.title}'") },
                    text = {
                        if (uiState.isSharingLoading) {
                            Box(modifier = Modifier.fillMaxWidth().height(200.dp), contentAlignment = Alignment.Center) {
                                CircularProgressIndicator()
                            }
                        } else {
                            Column {
                                // --- 2-COLUMN LAYOUT ---
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    // LEFT COLUMN
                                    Column(modifier = Modifier.weight(1f)) {
                                        ExportCheckbox(
                                            title = "Include Sections",
                                            checked = options.includeSections,
                                            onCheckedChange = { viewModel?.onSongToTextOptionsChange(includeSections = it) }
                                        )
                                        ExportCheckbox(
                                            title = "Include Guitar Info",
                                            checked = options.includeGuitarInfo,
                                            onCheckedChange = { viewModel?.onSongToTextOptionsChange(includeGuitar = it) }
                                        )
                                        // Guitar Tabs (Special Case)
                                        ExportCheckbox(
                                            title = "Include Tabs",
                                            checked = options.includeTabs,
                                            onCheckedChange = { viewModel?.onSongToTextOptionsChange(includeTabs = it) },
                                            enabled = canExportTabs,
                                            subtitle = if (!canExportTabs) "Creator+ Feature" else null
                                        )
                                    }

                                    // RIGHT COLUMN
                                    Column(modifier = Modifier.weight(1f)) {
                                        ExportCheckbox(
                                            title = "Include Chord Labels",
                                            checked = options.includeChordLabels,
                                            onCheckedChange = { viewModel?.onSongToTextOptionsChange(includeChords = it) }
                                        )
                                        ExportCheckbox(
                                            title = "Include Piano Info",
                                            checked = options.includePianoInfo,
                                            onCheckedChange = { viewModel?.onSongToTextOptionsChange(includePiano = it) }
                                        )
                                        ExportCheckbox(
                                            title = "Include Song Notes",
                                            checked = options.includeNotes,
                                            onCheckedChange = { viewModel?.onSongToTextOptionsChange(includeNotes = it) }
                                        )
                                    }
                                }
                                Spacer(Modifier.height(16.dp))

                                // Preview Box
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(250.dp)
                                        .border(1.dp, MaterialTheme.colorScheme.outline, RoundedCornerShape(4.dp))
                                ) {
                                    val hScroll = rememberScrollState()
                                    val vScroll = rememberScrollState()

                                    BasicTextField(
                                        value = generatedText,
                                        onValueChange = {},
                                        readOnly = true,
                                        textStyle = TextStyle(
                                            fontFamily = FontFamily.Monospace,
                                            fontSize = 12.sp,
                                            color = MaterialTheme.colorScheme.onSurface
                                        ),
                                        modifier = Modifier
                                            .fillMaxSize()
                                            .horizontalScroll(hScroll)
                                            .verticalScroll(vScroll)
                                            .padding(8.dp)
                                    )
                                }
                            }
                        }
                    },
                    confirmButton = {
                        Button(onClick = {
                            platformUtils.copyToClipboard(generatedText)
                            viewModel?.onSongToTextDismiss()
                        }) {
                            Icon(Icons.Default.ContentCopy, contentDescription = null, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(8.dp))
                            Text("Copy & Close")
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { viewModel?.onSongToTextDismiss() }) {
                            Text("Cancel")
                        }
                    }
                )
            }

            // Upgrade Dialog (Stub - can be expanded)
            if (uiState.showUpgradeDialog) {
                AlertDialog(
                    onDismissRequest = { viewModel?.onUpgradeDialogDismiss() },
                    title = { Text("Premium Feature") },
                    text = { Text(uiState.upgradeMessage ?: "This is a premium feature. Please upgrade to unlock all export options and cloud storage.") },
                    confirmButton = {
                        Button(onClick = { 
                            viewModel?.onUpgradeDialogDismiss()
                            onNavigateToSettings() // Navigate to settings/subscription
                        }) {
                            Text("Upgrade")
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { viewModel?.onUpgradeDialogDismiss() }) {
                            Text("Not Now")
                        }
                    }
                )
            }

            // Share Dialog
            if (uiState.shareDialogSong != null) {
                ShareSongDialog(
                    song = uiState.shareDialogSong!!,
                    onDismiss = { viewModel?.onShareDialogDismiss() },
                    onAddCollaborator = { email: String, isEditor: Boolean -> 
                        viewModel?.addCollaborator(email, isEditor, subscriptionTier) 
                    },
                    onRemoveCollaborator = { uid: String -> viewModel?.removeCollaborator(uid) },
                    onReportCollaborator = { uid: String -> viewModel?.reportCollaborator(uid) },
                    onBlockCollaborator = { uid: String -> viewModel?.blockCollaborator(uid) },
                    isLoading = uiState.isSharingLoading,
                    lookupError = uiState.shareError,
                    subscriptionTier = subscriptionTier,
                    onUpgrade = { 
                        viewModel?.onShareDialogDismiss()
                        onNavigateToSettings()
                    }
                )
            }

            // Delete Confirmation Dialog
            if (uiState.songToDelete != null) {
                val isShared = uiState.songToDelete!!.ownerId != uiState.currentUserId && uiState.songToDelete!!.ownerId.isNotEmpty()
                DeleteConfirmationDialog(
                    title = if (isShared) "Remove Song?" else "Delete Song?",
                    text = if (isShared) "Remove \"${uiState.songToDelete!!.title}\" from your list?" else "Are you sure you want to delete \"${uiState.songToDelete!!.title}\"?",
                    onConfirm = { viewModel?.confirmSongDeletion() },
                    onDismiss = { viewModel?.cancelSongDeletion() }
                )
            }

            // Export Progress
            if (isExporting) {
                LoadingOverlay(message = "Exporting File...")
            }
            
            // Error Dialog (Simple)
            if (exportError != null) {
                // Could act as a Snackbar, but Dialog for now
                // We need a way to clear error. ViewModel doesn't have clearError?
                // Assuming it clears on next action or we can just ignore for now or add a timeout.
            }

            // Daily Reminder Prompt
            if (showDailyReminderPrompt) {
                AlertDialog(
                    onDismissRequest = { showDailyReminderPrompt = false },
                    title = { Text("Daily Reminders") },
                    text = { Text("Would you like to enable daily reminders to help you stay consistent with your songwriting?") },
                    confirmButton = {
                        Button(onClick = {
                            viewModel?.toggleDailyReminder(true)
                            showDailyReminderPrompt = false
                        }) {
                            Text("Enable")
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { 
                            viewModel?.toggleDailyReminder(false)
                            showDailyReminderPrompt = false 
                        }) {
                            Text("Not Now")
                        }
                    }
                )
            }
        }
    }
}

@Composable
fun InvitationsSection(viewModel: HomeViewModel?, subscriptionTier: songwritersnotebook.app.billing.SubscriptionTier) {
    val invitations = viewModel?.invitations?.collectAsState()?.value ?: emptyList()
    val uiState = viewModel?.uiState?.collectAsState()?.value ?: HomeUiState()
    
    if (invitations.isNotEmpty()) {
        Column(modifier = Modifier.padding(bottom = 16.dp)) {
            Text(
                "Invitations",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.padding(bottom = 8.dp)
            )
            invitations.forEach { invite ->
                Card(
                    modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
                ) {
                    Row(
                        modifier = Modifier.padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Column(modifier = Modifier.weight(1f)) {
                            Text(invite.songTitle, fontWeight = FontWeight.Bold)
                            Text("From: ${invite.ownerEmail}", style = MaterialTheme.typography.bodySmall)
                            Text("Role: ${invite.role.replaceFirstChar { it.uppercase() }}", style = MaterialTheme.typography.bodySmall)
                        }
                        Row {
                            if (uiState.isRespondingToInvitation) {
                                CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                            } else {
                                IconButton(onClick = { viewModel?.acceptInvitation(invite, subscriptionTier) }) {
                                    Icon(Icons.Default.Check, "Accept", tint = MaterialTheme.colorScheme.primary)
                                }
                                IconButton(onClick = { viewModel?.rejectInvitation(invite) }) {
                                    Icon(Icons.Default.Close, "Reject", tint = MaterialTheme.colorScheme.error)
                                }
                            }
                        }
                    }
                }
            }
            HorizontalDivider()
        }
    }
}

@Composable
fun ExportCheckbox(
    title: String,
    checked: Boolean,
    enabled: Boolean = true,
    subtitle: String? = null,
    onCheckedChange: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(enabled = enabled) { onCheckedChange(!checked) }
            .padding(vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = null, // Handled by Row click
            enabled = enabled
        )
        Spacer(modifier = Modifier.width(8.dp))
        Column {
            Text(
                text = title,
                style = MaterialTheme.typography.bodyMedium,
                color = if (enabled) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
            )
            if (subtitle != null) {
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.primary
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HomeSearchBar(
    query: String,
    onQueryChange: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        placeholder = { Text("Search songs...") },
        leadingIcon = { Icon(Icons.Default.Search, contentDescription = null) },
        trailingIcon = {
            if (query.isNotEmpty()) {
                IconButton(onClick = { onQueryChange("") }) {
                    Icon(Icons.Default.Close, contentDescription = "Clear search")
                }
            }
        },
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp)
            .then(modifier),
        shape = RoundedCornerShape(8.dp),
        singleLine = true,
        colors = TextFieldDefaults.colors(
            unfocusedContainerColor = MaterialTheme.colorScheme.surface,
            focusedContainerColor = MaterialTheme.colorScheme.surface,
        )
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HomeTopBar() {
    TopAppBar(
        title = { Text("Songwriter's Notebook", fontWeight = FontWeight.Bold) },
        navigationIcon = {
            Box(
                modifier = Modifier
                    .padding(start = 12.dp, end = 6.dp)
                    .size(36.dp)
                    .background(color = Color(0xFF3B82F6))
            ) {
                Image(
                    painter = painterResource(Res.drawable.ic_logo_fg),
                    contentDescription = "App Icon",
                    modifier = Modifier
                        .fillMaxSize()
                        .scale(1.2f)
                )
            }
        },
        colors = TopAppBarDefaults.topAppBarColors(
            containerColor = MaterialTheme.colorScheme.background
        )
    )
}

@Composable
fun SongListItem(
    song: Song,
    onSongClick: () -> Unit,
    onSongDeleteRequest: () -> Unit,
    onSongToTextRequest: () -> Unit = {},
    onSongExportRequest: () -> Unit = {},
    onSongShareRequest: () -> Unit = {},
    isMenuExpanded: Boolean,
    onSlideMenuToggled: () -> Unit,
    isTutorialSong: Boolean = false,
    currentUserId: String? = null,
    subscriptionTier: songwritersnotebook.app.billing.SubscriptionTier = songwritersnotebook.app.billing.SubscriptionTier.FREE
) {
    val isShared = song.ownerId.isNotEmpty() && song.ownerId != currentUserId
    val isViewer = isShared && song.viewers.contains(currentUserId)

    BoxWithConstraints {
        val maxWidth = this.maxWidth

        Card(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(enabled = !isMenuExpanded, onClick = onSongClick)
                .then(if (isTutorialSong) Modifier.tutorialTarget(TutorialStep.HOME_SONG_CARD).tutorialTarget(TutorialStep.HOME_TAP_SONG) else Modifier),
            shape = RoundedCornerShape(8.dp),
            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(IntrinsicSize.Min),
                contentAlignment = Alignment.CenterStart
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(song.title, fontWeight = FontWeight.SemiBold, fontSize = 16.sp)
                        Spacer(modifier = Modifier.height(4.dp))
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Text(
                                text = formatDate(song.lastModified),
                                fontSize = 12.sp,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Spacer(Modifier.width(8.dp))
                            
                            if (isShared) {
                                // Shared Song Icons
                                val isOnline by remember { songwritersnotebook.app.utils.NetworkUtils().networkStatus }.collectAsState(initial = true)
                                Icon(
                                    if (isOnline) Icons.Default.Cloud else Icons.Default.CloudOff, 
                                    if (isOnline) "Cloud" else "Offline", 
                                    tint = if (isOnline) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f), 
                                    modifier = Modifier.size(14.dp)
                                )
                                if (song.ownerUsername.isNotEmpty()) {
                                    Spacer(Modifier.width(4.dp))
                                    Text(
                                        text = "@${song.ownerUsername}",
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                }
                                Spacer(Modifier.width(4.dp))
                                if (isViewer) {
                                    Icon(Icons.Default.Visibility, "Viewer", tint = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.size(14.dp))
                                } else {
                                    Icon(Icons.Default.Edit, "Editor", tint = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.size(14.dp))
                                }
                            } else {
                                // Local/Owned Icons
                                Icon(Icons.Default.Save, "Local", tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(14.dp))
                                
                                if (song.ownerId.isNotEmpty()) {
                                    // Only show cloud status if Studio Tier
                                    if (subscriptionTier == songwritersnotebook.app.billing.SubscriptionTier.STUDIO) {
                                        val isOnline by remember { songwritersnotebook.app.utils.NetworkUtils().networkStatus }.collectAsState(initial = true)
                                        Spacer(Modifier.width(4.dp))
                                        when {
                                            !isOnline -> Icon(Icons.Default.CloudOff, "Offline", tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f), modifier = Modifier.size(14.dp))
                                            song.isSyncError -> Icon(Icons.Default.CloudOff, "Sync Error", tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(14.dp))
                                            song.lastSynced != null && song.lastSynced >= song.lastModified -> Icon(Icons.Default.CloudDone, "Synced", tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(14.dp))
                                            else -> Icon(Icons.Default.CloudUpload, "Sync Pending", tint = MaterialTheme.colorScheme.tertiary, modifier = Modifier.size(14.dp))
                                        }
                                    }
                                    
                                    // Collaborator Icon (Owner View) - Show if any collaborators exist
                                    if (song.collaborators.isNotEmpty() || song.viewers.isNotEmpty()) {
                                         Spacer(Modifier.width(4.dp))
                                         Icon(Icons.Default.Group, "Collaborators", tint = MaterialTheme.colorScheme.secondary, modifier = Modifier.size(14.dp))
                                    }
                                }
                            }
                        }
                    }
                    IconButton(
                        onClick = onSlideMenuToggled,
                        modifier = if (isTutorialSong) Modifier.tutorialTarget(TutorialStep.HOME_SONG_CONTEXT) else Modifier
                    ) {
                        Icon(
                            imageVector = Icons.Default.MoreVert,
                            contentDescription = "More options"
                        )
                    }
                }

                val animatedOffsetX by animateDpAsState(
                    targetValue = if (isMenuExpanded) 0.dp else -maxWidth
                )

                if (isMenuExpanded) {
                    Surface(
                        modifier = Modifier
                            .fillMaxSize()
                            .offset(x = animatedOffsetX),
                        color = MaterialTheme.colorScheme.surface
                    ) {
                        Row(
                            horizontalArrangement = Arrangement.SpaceEvenly,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            if (!isViewer) {
                                // Text Export
                                Surface(
                                    shape = RoundedCornerShape(4.dp),
                                    modifier = Modifier.size(48.dp).clickable { onSongToTextRequest() },
                                    color = Color.Transparent
                                ) {
                                    Column(
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.Center
                                    ) {
                                        Icon(Icons.AutoMirrored.Filled.Article, "Export Text")
                                        Text("Text", fontSize = 10.sp)
                                    }
                                }

                                // Audio Export
                                Surface(
                                    shape = RoundedCornerShape(4.dp),
                                    modifier = Modifier.size(48.dp).clickable { onSongExportRequest() },
                                    color = Color.Transparent
                                ) {
                                    Column(
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.Center
                                    ) {
                                        Icon(Icons.Default.AudioFile, "Export Audio")
                                        Text(".m4a", fontSize = 10.sp)
                                    }
                                }
                            }
                            
                            // Share (Only Owner)
                            if (!isShared) {
                                Surface(
                                    shape = RoundedCornerShape(4.dp),
                                    modifier = Modifier.size(48.dp).clickable { onSongShareRequest() },
                                    color = Color.Transparent
                                ) {
                                    Column(
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.Center
                                    ) {
                                        Icon(Icons.Default.Share, "Share")
                                        Text("Share", fontSize = 10.sp)
                                    }
                                }
                            }

                            // Delete / Remove
                            Surface(
                                shape = RoundedCornerShape(4.dp),
                                modifier = Modifier.size(48.dp).clickable { onSongDeleteRequest() },
                                color = Color.Transparent
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    // Icon depending on shared state
                                    if (isShared) {
                                         Icon(Icons.Default.Close, "Remove", tint = MaterialTheme.colorScheme.error)
                                    } else {
                                         Icon(Icons.Default.Delete, "Delete", tint = MaterialTheme.colorScheme.error)
                                    }
                                }
                            }
                            
                            IconButton(onClick = onSlideMenuToggled) {
                                Icon(Icons.Default.Close, "Close")
                            }
                        }
                    }
                }
            }
        }
    }
}

fun formatDate(instant: Instant): String {
    val dt = instant.toLocalDateTime(TimeZone.currentSystemDefault())
    val monthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    val month = monthNames[dt.monthNumber - 1]
    val amPm = if (dt.hour >= 12) "PM" else "AM"
    val hour12 = if (dt.hour > 12) dt.hour - 12 else if (dt.hour == 0) 12 else dt.hour
    val minute = dt.minute.toString().padStart(2, '0')
    return "$month ${dt.dayOfMonth}, ${dt.year}, $hour12:$minute $amPm"
}
